import { combineLatest, defer, Observable, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  shareReplay,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';

import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';

import { NzTableComponent, NzTableLayout } from 'ng-zorro-antd/table';

import { FadeAnimation } from '@app/core/animations/fade.transition';
import { CommonAPI } from '@app/core/api/common.service';
import { FilterOption, ReusableTableConfiguration } from '@app/core/models/common.models';
import { LifecycleHooks } from '@app/core/services/lifecycle-hooks.service';
import { isArrayShallowEqual } from '@app/core/utilities/array.utilities';
import { arraySnakeToCamelCase, camelToSnake } from '@app/core/utilities/string.utilities';
import { environment } from '@env/environment.prod';

/**
 * Reusable Table
 * Usage:
 * <adspt-table
 *    [config]="config"
 *    [actionsTemplate]="actionsTmpl"
 *    [expandTemplate]="expandTmpl"
 *    [dataTransformation]="transformation">
 * </adspt-table>
 */
@Component({
  standalone: false,
  selector: 'adspt-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  providers: [LifecycleHooks],
  animations: [FadeAnimation],
})
export class TableComponent implements OnInit, OnDestroy, OnChanges {
  $;
  /**
   * Input  of table component:
   * configuration object, check the types above
   */
  @Input() config: ReusableTableConfiguration;
  /**
   * Input of table component:
   * pass the template for actions columns, if not set, actions column will not be shown
   */
  @Input() actionsTemplate: TemplateRef<any>;
  /**
   * Input of table component:
   * pass the template for expanded row, if not set, component will render a normal table
   */
  @Input() expandTemplate: TemplateRef<any>;
  @Input() noResultTemplate: string | TemplateRef<any>;
  @Input() columnTemplates = {};
  @Input() headerTemplates = {};
  @Input() hasInnerScroll = false;
  @Input() title = true;
  @Input() footer = true;
  @Input() border = true;
  @Input() resetOnConfigChange = false;
  /**
   * Optional Input of table component:
   * if the response needs to be transformed, eg formatting data etc.,
   * create a transformation function, response data is sent into argument
   * should return array of objects with the same data structure
   */
  @Input() dataTransformation?: (data: any[]) => any[];
  @Input() heapSearchSelector?: string;
  @Input() heapFilterSelectors: Record<string, string> = {};
  @Input() tableLayout: NzTableLayout = 'auto';

  @Output() dataLoaded = new EventEmitter<{ url: string; refiners: [string, string][]; limit: number }>();
  @ViewChild('adsptTable', { static: false }) adsptTableEle: NzTableComponent<undefined | unknown[]>;
  sortOrder = {} as any;
  loading = true;
  intialProcessing = true;
  totalData = [];
  data: any[] = [];
  sizeChanger = true;
  pagination = true;
  header = true;
  columnsWidths = [];
  total: number;
  pageIndex = 1;
  pageSize = 20;
  query = new FormControl('');
  sorting = [];
  filter = {};
  expandRowColspan = 0;
  hasActions = false;
  expandable = false;
  selection = false;
  isAllDisplayDataChecked = false;
  isIndeterminate = false;
  scroll: any = {};

  readonly filters$ = defer(() =>
    combineLatest(
      this.config.columns.map((col) =>
        (col.filterOptions as Observable<FilterOption[]>).pipe(
          tap((filterOptions) => (this.filter[col.key] = filterOptions.filter((d) => d.byDefault).map((d) => d.value))),
        ),
      ),
    ),
  ).pipe(
    map(() => void 0),
    shareReplay(1),
    takeUntil(this.lh.destroy$),
  );

  makeRequest() {
    this.$?.unsubscribe();
    this.$ = this._getData(this.query.value).subscribe();
  }

  onFilter(data: [], key) {
    if (!isArrayShallowEqual(data, this.filter[key])) {
      this.pageIndex = 1;
      this.filter[key] = [...data];
      this.makeRequest();
    }
  }
  onSort(event: { key: string; value: any }) {
    if (this.config.singleSort) {
      this.sorting = event.value ? [event] : [];
    } else {
      if (event.value) {
        if (this.sorting.filter((d) => d.key === event.key).length === 0) {
          this.sorting.push({ key: event.key, value: event.value });
        } else {
          this.sorting.forEach((d) => {
            if (d.key === event.key) {
              d.value = event.value;
            }
          });
        }
      } else {
        this.sorting = this.sorting.filter((d) => d.key !== event.key);
      }
      this._setOrder();
    }
    this.makeRequest();
  }
  onPageIndexChange(event: number) {
    this.pageIndex = event;
    this._scrollToTop();
    this.makeRequest();
  }
  onPageSizeChange(event: number) {
    this.pageSize = event;
    this._scrollToTop();
    this.makeRequest();
  }
  onRowChecked() {
    this._emitCheckedChanges();
  }
  onAllChecked(data: boolean) {
    this.data.forEach((d) => (d.checked = data));
    this._emitCheckedChanges();
  }
  onExpandChanged(data) {
    if (!!this.config.onExpandChanged) {
      this.config.onExpandChanged(data);
    }
  }
  getColumnSort(column: string): 'ascend' | 'descend' | null {
    const sort = this.sorting.find(({ key }) => key === column);
    return (sort && sort.value) || null;
  }

  private _setOrder() {
    this.sortOrder = {};
    let counter = 0;
    this.sorting.forEach((value, key) => {
      const t = !Array.isArray(this.sorting) ? value : value.key;
      this.sortOrder[t] = ++counter;
    });
  }
  private _scrollToTop() {
    if (this.adsptTableEle.nzTableInnerScrollComponent) {
      this.adsptTableEle.nzTableInnerScrollComponent.tableBodyElement.nativeElement.scrollTop = 0;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  private _emitCheckedChanges() {
    if (!!this.config.onSelectionChanged) {
      const checkedRows = this.data.filter((d) => d.checked);
      this.isAllDisplayDataChecked = checkedRows.length === this.pageSize;
      this.isIndeterminate = !!checkedRows.length
        ? !this.isAllDisplayDataChecked && checkedRows.length < this.pageSize
        : false;
      this.config.onSelectionChanged(checkedRows);
    }
  }

  private _getData(query: string): Observable<any> {
    this.loading = true;
    this.cd.detectChanges();
    const { refiners, refinersForExportConfig } = this._getRefiners(query);

    return this.filters$.pipe(
      take(1),
      switchMap(() =>
        this.commonAPI
          .makeRequest(this.config.endpoint, refiners, this.config.accessLevel)
          .pipe(catchError(() => of({ data: [], total: 0 }))),
      ),
      map((response) => {
        if (this.config.camelTransform) response.data = arraySnakeToCamelCase(response.data);

        this.data = this.dataTransformation(response.data);
        this.total = response.total;
        this.loading = false;
        this.dataLoaded.emit({
          url: `${environment.app.resources.dashboard}${this.config.endpoint}`,
          refiners: refinersForExportConfig,
          limit: this.total,
        });

        this.intialProcessing = false;
        this.cd.markForCheck();

        return response;
      }),
      tap((response) => this.config.onData?.(response.data)),
    );
  }

  private _getRefiners(query): { refinersForExportConfig: [string, string][]; refiners: string } {
    const refiners =
      this.config.defaultRefiners && this.config.defaultRefiners.length ? [...this.config.defaultRefiners] : [];

    // SORTING
    const sortRefiners = this.config?.sortTransformer ? this.config?.sortTransformer(this.sorting) : this.sorting;
    sortRefiners.forEach((d, i) => {
      refiners.push({
        key: `sort_${this.config.camelTransform ? camelToSnake(d.key) : d.key}`,
        value: d.value === 'ascend' ? i + 1 : -(i + 1),
      });
    });

    // FILTERING
    for (const filter in this.filter) {
      if (!!this.filter[filter].length) {
        refiners.push({
          key: 'filter_' + (this.config.camelTransform ? camelToSnake(filter) : filter),
          value: this.filter[filter].join(','),
        });
      }
    }
    if (!!query && !!this.config.search) {
      refiners.push({
        key: 'search_fields',
        value: this.config.search.keys.map((key) => (this.config.camelTransform ? camelToSnake(key) : key)).join(','),
      });
      refiners.push({
        key: 'search_term',
        value: encodeURIComponent(query),
      });
    }
    const refinersForExportConfig = <[string, string][]>refiners.map(({ key, value }) => [key, value]);

    refiners.push(
      {
        key: 'limit',
        value: this.pageSize,
      },
      {
        key: 'offset',
        value: (this.pageIndex - 1) * this.pageSize,
      },
      {
        key: 'include',
        value: [
          ...this.config.includeExtraData.map((extra) => (this.config.camelTransform ? camelToSnake(extra) : extra)),
          ...this.config.columns
            .filter((d) => !!d.key && !d.excludeInURI)
            .map((col) => (this.config.camelTransform ? camelToSnake(col.key) : col.key)),
        ].join(','),
      },
    );

    return { refinersForExportConfig, refiners: refiners.map((d) => `${d.key}=${d.value}`).join('&') };
  }
  private _validateConfiguration() {
    this.hasActions = !!this.actionsTemplate;
    this.expandable = !!this.expandTemplate;
    this.selection = !!this.config.onSelectionChanged;
    this.pageSize = !this.config.pageSize ? 10 : this.config.pageSize;
    this.scroll = { y: isNaN(this.config.height) ? '500px' : this.config.height + 'px' };
    if (!!this.config.width) {
      this.scroll.x = this.config.width + 'px';
    }
    if (this.selection) {
      this.columnsWidths.push('50px');
    }
    if (this.expandable) {
      this.columnsWidths.push('50px');
      this.expandRowColspan = this.config.columns.length + (this.hasActions ? 1 : 0) + (this.selection ? 1 : 0);
    }
    if (!this.config.includeExtraData) {
      this.config.includeExtraData = [];
    }
    this.config.columns.forEach((item: any) => {
      this.columnsWidths.push(!item.width ? null : item.width + 'px');
      item.align = !item.align ? 'left' : item.align;
      item.filterOptions =
        !item.filterable && !item.filterOptions
          ? of([])
          : Array.isArray(item.filterOptions)
            ? of(item.filterOptions)
            : item.filterOptions;
      if (item.filterable) {
        this.filter[item.key] = [];
      }
      item.tooltip = !item.tooltip ? '' : item.tooltip;
      if (item.defaultSort < 0) {
        this.sorting.push({ key: item.key, value: item.defaultSort });
        item.defaultSort = 'descend';
      } else if (item.defaultSort > 0) {
        this.sorting.push({ key: item.key, value: item.defaultSort });
        item.defaultSort = 'ascend';
      } else {
        item.defaultSort = null;
      }
      if (!this.config.singleSort) {
        this._setOrder();
      }
    });
    if (this.config.search && this.config.search.defaultQuery) {
      this.query.setValue(this.config.search.defaultQuery, { emitEvent: false });
    }
    if (this.hasActions) {
      this.columnsWidths.push((this.config.actionsColumnWidth ? this.config.actionsColumnWidth : 250) + 'px');
    }
    this.sorting = this.sorting.sort((a, b) => a.value - b.value);
    this.sorting.forEach((d) => (d.value = d.value > 0 ? 'ascend' : 'descend'));
  }

  constructor(
    private commonAPI: CommonAPI,
    private cd: ChangeDetectorRef,
    private lh: LifecycleHooks,
  ) {
    this.query.valueChanges
      .pipe(
        debounceTime(750),
        distinctUntilChanged(),
        switchMap((search) => {
          this.pageIndex = 1;

          if (this.config.onSearchChanged) {
            this.config.onSearchChanged(search);
          }

          return this._getData(search);
        }),
        takeUntil(this.lh.destroy$),
      )
      .subscribe();
  }

  refresh(): void {
    this.makeRequest();
  }

  search(query: string, resetFilters?: boolean): void {
    if (resetFilters) {
      this.filter = {};
    }
    this.pageIndex = 1;
    this.query.setValue(query, { emitModelToViewChange: true, emitViewToModelChange: false, emitEvent: false });
    this.refresh();
  }

  ngOnInit(): void {
    if (!this.dataTransformation) {
      this.dataTransformation = (data: any) => data;
    }
    this._validateConfiguration();
    this.makeRequest();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!!this.resetOnConfigChange && !!changes?.config?.currentValue && !changes?.config?.isFirstChange()) {
      this._validateConfiguration();
      this.makeRequest();
    }
  }

  ngOnDestroy() {
    this.$?.unsubscribe();
  }
}
